import {
  MenuItem,
  TableBody,
  TableCell,
  TableRow,
  ThemeProvider,
  Toolbar,
  createTheme,
} from "@mui/material";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import CancelOutlinedIcon from "@mui/icons-material/CancelOutlined";
import DeleteOutlinedIcon from "@mui/icons-material/DeleteOutlined";
import CheckCircleOutlinedIcon from "@mui/icons-material/CheckCircleOutlined";
import React, { useEffect, useState } from "react";
import Moment from "react-moment";
import Loader from "../../../shared/components/Loader";
import { AdminCustomersState, Customer, CustomersQueryParameter } from "../../models/AdminCustomersModels";
import { useAppDispatch, useAppSelector } from "../../../shared/hooks/sharedHooks";
import ActionToast from "../../../shared/components/ActionToast";
import { changeCustomerStatus, changeQuery, deleteCustomer, getCustomers, selectCustomer, setSuccess } from "../../adminCustomersSlice";
import ActiveDisabledStatus from "../../../shared/components/ActiveDisabledStatus";
import Table from "../../../shared/components/tables/Table";
import SortTableHead, { TableColumnDefinition } from "../../../shared/components/SortTableHead";
import TablePagination from "../../../shared/components/tables/TablePagination";
import TableActionButton from "../../../shared/components/tables/TableActionButton";
import { useConfirmation } from "../../../shared/components/confirmation/ConfirmationProvider";
import AdminSearch from "../AdminSearch";
import CustomerDialog from "./CustomerDialog";

const Customers = () => {
  const { query, customers, customer, loading, success, error } = useAppSelector<AdminCustomersState>(
    (state: any) => state.adminCustomers
  );

  const dispatch = useAppDispatch();
  const confirm = useConfirmation();
  const [isCustomerDialog, setIsCustomerDialog] = useState(false);

  const columns: TableColumnDefinition[] = [
    { title: "Company", parameter: CustomersQueryParameter.Name },
    { title: "Role", parameter: CustomersQueryParameter.Role },
    { title: "Created", parameter: CustomersQueryParameter.CreatedDate },
    { title: "Last activity", parameter: CustomersQueryParameter.ModifiedDate },
    { title: "Status" },
    { title: undefined }
  ];

  const handleSearch = (value?: string) => {
    const newQuery = { ...query, search: value, pageNo: 1 };
    dispatch(changeQuery(newQuery));
    dispatch(getCustomers(newQuery));
  };

  const handleSort = (parameter: number) => {
    const sortDesc = query.sortBy !== parameter ? false : !query.sortDesc;
    const newQuery = { ...query, sortBy: parameter, sortDesc: sortDesc };

    dispatch(changeQuery(newQuery))
    dispatch(getCustomers(newQuery));
  };

  const handleEdit = (customer: Customer) => {
    dispatch(selectCustomer(customer));
    setIsCustomerDialog(true);
  };

  const handleChangeStatus = async (customer: Customer, isDisabled: boolean) => {
    if (customer) {
      await dispatch(changeCustomerStatus({ id: customer.id, isDisabled }));
      dispatch(setSuccess(`Customer ${isDisabled ? "disabled" : "activated"} successfully`));
      await dispatch(getCustomers(query));
    }
  };

  const handleDelete = async (customer: Customer) => {
    if (customer) {
      await dispatch(deleteCustomer(customer.id));
      await dispatch(getCustomers(query));
    }
  };

  const handlePageSizeChange = async (size: number) => {
    const newQuery = { ...query, pageNo: 1, pageSize: size };
    await dispatch(changeQuery(newQuery));
    dispatch(getCustomers(newQuery));
  };

  const handlePageNoChange = async (no: number) => {
    const newQuery = { ...query, pageNo: no };
    await dispatch(changeQuery(newQuery));
    dispatch(getCustomers(newQuery));
  };

  useEffect(() => {
    dispatch(getCustomers(query));
  }, []);

  const theme = createTheme({
    components: {
      MuiToolbar: {
        styleOverrides: {
          root: {
            background: "#fff",
            borderBottom: "1px solid #d5d6d9",
          },
        },
      },
      MuiInputBase: {
        styleOverrides: {
          root: {
            boderRadius: "6px 0 0 6px !important",
          },
        },
      },
    },
  });

  if (loading) {
    return <Loader />;
  }
  return (
    <ThemeProvider theme={theme}>
      <ActionToast
        open={!!success}
        variant="success"
        message={success || ""}
        onClose={() => dispatch(setSuccess(undefined))}
        duration={6000}
      />

      <Toolbar className="flex items-center justify-between w-full">
        <div className="flex items-center">
          <p className="heading-2">Customers</p>
          <div className="ml-7">
            <AdminSearch value={query.search} onSearch={handleSearch} />
          </div>
        </div>
      </Toolbar>

      <Table className="px-3 v-fill">
        <SortTableHead columns={columns} sortBy={query.sortBy} sortDesc={query.sortDesc} onSort={handleSort} />

        <TableBody>
          {customers?.items.map((customer) => (
            <TableRow key={customer.id}>
              <TableCell>{customer.name}</TableCell>

              <TableCell>{customer.role?.name || ''}</TableCell>

              <TableCell>
                <Moment date={customer.createdDate} format="DD.MM.YYYY HH:mm" />
              </TableCell>

              <TableCell>
                <Moment date={customer.modifiedDate} format="DD.MM.YYYY HH:mm" />
              </TableCell>

              <TableCell>
                <ActiveDisabledStatus disabled={customer.isDisabled} />
              </TableCell>

              <TableCell>
                <TableActionButton>
                  <MenuItem onClick={() => handleEdit(customer)}>
                    <EditOutlinedIcon />
                    Edit
                  </MenuItem>

                  {customer?.isDisabled ? (
                    <MenuItem onClick={() => handleChangeStatus(customer, false)}>
                      <CheckCircleOutlinedIcon />
                      Enable
                    </MenuItem>
                  ) : (
                    <MenuItem onClick={() => handleChangeStatus(customer, true)}>
                      <CancelOutlinedIcon />
                      Disable
                    </MenuItem>
                  )}

                  <MenuItem onClick={() => confirm({
                    title: "Delete customer",
                    text: `Are you sure you want to delete ${customer?.name}?`,
                    confirmButton: "Yes, delete",
                    closeButton: "No",
                    severity: "error",
                    onConfirm: () => handleDelete(customer)
                  })}>
                    <DeleteOutlinedIcon />
                    Delete
                  </MenuItem>
                </TableActionButton>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>

      <TablePagination
        variant="bottom"
        pageNo={query.pageNo}
        pageSize={query.pageSize}
        totalCount={customers?.totalCount}
        onPageNoChange={handlePageNoChange}
        onPageSizeChange={handlePageSizeChange} />

      {customer && (
        <CustomerDialog
          key={`customer-dialog-${isCustomerDialog}`}
          open={isCustomerDialog}
          onClose={() => {
            setIsCustomerDialog(false);
            dispatch(getCustomers(query));
          }}
          customer={customer}
        />
      )}
    </ThemeProvider>
  );
};
export default Customers;
